import { useEffect, useRef } from 'react'
import Pusher from 'pusher-js'
import { PusherNotificationEvent } from 'types/notification'

const normalizeStoreIds = (storeIds: string[]) => {
  if (storeIds.includes('any')) {
    return ['any']
  }

  return Array.from(new Set(storeIds))
}

export const usePusher = (
  storeIds: string[],
  onNotification: (event: PusherNotificationEvent) => void
) => {
  const pusherRef = useRef<Pusher | null>(null)
  const onNotificationRef = useRef(onNotification)

  useEffect(() => {
    onNotificationRef.current = onNotification
  }, [onNotification])

  const eventName = (storeId: string) => {
    if (storeId === 'any') {
      return 'place:events'
    }
    return `place:${storeId}:events`
  }

  useEffect(() => {
    // Check if Pusher is configured
    const pusherKey = window.appConfig.PUSHER_KEY
    const pusherCluster = window.appConfig.PUSHER_CLUSTER
    const pusherChannel = window.appConfig.PUSHER_CHANNEL
    const notificationsEnabled = window.appConfig.ENABLE_NOTIFICATIONS

    if (
      !pusherKey ||
      !pusherCluster ||
      !pusherChannel ||
      !notificationsEnabled ||
      !storeIds ||
      storeIds.length === 0
    ) {
      return
    }

    // Initialize Pusher
    pusherRef.current = new Pusher(pusherKey, {
      cluster: pusherCluster,
    })

    // eslint-disable-next-line no-console
    console.log('Pusher initializing for stores:', storeIds)

    if (pusherRef.current) {
      const channel = pusherRef.current.subscribe(pusherChannel)

      normalizeStoreIds(storeIds).forEach((storeId) => {
        // eslint-disable-next-line no-console
        console.log(
          `Binding to Pusher: ${pusherChannel} -> ${eventName(storeId)}`
        )
        channel.bind(eventName(storeId), (data: PusherNotificationEvent) => {
          onNotificationRef.current(data)
        })
      })
    }

    // eslint-disable-next-line consistent-return
    return () => {
      if (pusherRef.current) {
        if (!pusherRef.current) return

        pusherRef.current.unsubscribe(pusherChannel)
        pusherRef.current.disconnect()
      }
    }
  }, [storeIds])
}
